/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import styles from './Comments.module.scss';
import {
  CommentType,
  addReviewCommentsThunk,
  fetchPageCommentsThunk,
  selectComments,
} from '../features/Comments/commentsSlice';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { TransitionGroup } from 'react-transition-group';
import { MessageType, PageType, PageTypeEnum } from '../features/sharedcomponents/algorithm/types';
import { selectTabPageSelected } from '../features/sharedcomponents/chat/chatSlice';
import { useEffect, useMemo, useState } from 'react';

interface NewGuideCommentProps {
  pageId?: string;
  reviewId?: string;
  documentVersionId?: string;
  className?: string;
  placeholder?: string;
}
type NewComment = {
  text: string;
  processing: boolean;
};
export default function NewGuideComment({
  pageId,
  reviewId,
  documentVersionId,
  className,
  placeholder,
}: NewGuideCommentProps) {
  const dispatch = useAppDispatch();

  const [newComment, setNewComment] = useState<NewComment>({
    text: '',
    processing: false,
  });

  const isDisabled = useMemo(
    () => !newComment.text.trim() || newComment.processing || !pageId || !reviewId || !documentVersionId,
    [newComment.text, newComment.processing, pageId, reviewId, documentVersionId]
  );

  async function handleClick() {
    if (!pageId || !reviewId || !documentVersionId) return;

    try {
      setNewComment((s) => ({ ...s, processing: true }));
      await dispatch(
        addReviewCommentsThunk({
          pageId,
          reviewId,
          documentVersionId,
          content: newComment.text.trim(),
        })
      ).unwrap();
      setNewComment({ text: '', processing: false });
      await dispatch(fetchPageCommentsThunk(pageId));
    } catch (err) {
      setNewComment((s) => ({ ...s, processing: false }));
      // optionally log err
      console.error(err);
    }
  }

  return (
    <div className={`${styles.container} ${styles.newComment} ${className ?? ''}`}>
      <TextField
        multiline
        value={newComment.text}
        disabled={newComment.processing}
        onChange={(e) => setNewComment({ text: e.target.value, processing: false })}
        placeholder={placeholder}
        className={styles.textfield}
        variant="standard"
        InputProps={{ disableUnderline: true }}
      />
      <div className={styles.dateAndButton}>
        <Button className={styles.button} onClick={handleClick} disabled={isDisabled} variant="flat" color="secondary">
          Submit
        </Button>
      </div>
    </div>
  );
}

interface GuideCommentProps {
  className?: string;
  comment: CommentType;
}
export function GuideComment({ className, comment }: GuideCommentProps) {
  return (
    <div className={`${styles.container} ${className}`}>
      <span className={styles.person_name}>{comment?.user.name}</span>
      &nbsp;
      {comment?.content}
      <div className={styles.dateAndButton}>
        <div className={styles.date}>
          <div className={styles.commentState}>
            Comment on state
            {comment.documentVersion.state}
          </div>
        </div>
      </div>
    </div>
  );
}
export function ReviewCommentContainer({ reviewId, message }: { reviewId?: string; message?: MessageType }) {
  const document_version_id = message?.pages?.[0]?.document_version_id;
  let page_ = message?.pages?.[0];
  const tabPageSelected = useAppSelector((state) =>
    document_version_id && page_?.id ? selectTabPageSelected(state, document_version_id, page_?.id) : undefined
  );
  const page: PageType | undefined = message?.pages?.[0]?.pageType === PageTypeEnum.TABS ? tabPageSelected : page_;
  const fetchedComments = useAppSelector((state) => (page?.id ? selectComments(state, page.id) : undefined));
  const dispatch = useAppDispatch();
  useEffect(() => {
    // Fetch comments with the current pageId.
    if (page?.id) {
      void dispatch(fetchPageCommentsThunk(page.id));
    }
  }, [dispatch, page?.id]);

  if (!message?.pages || message.is_sent) return null;
  console.log('page in review container', page?.id);
  return (
    <>
      {' '}
      <NewGuideComment
        placeholder={`Comments or recommendations for  ${page?.name ?? 'current page'}`}
        pageId={page?.id}
        documentVersionId={document_version_id}
        reviewId={reviewId}
      />
      <List>
        <TransitionGroup>
          {(fetchedComments ?? [])
            .filter((comment) => comment.state === 'OPEN')
            .map((comment) => (
              <Collapse key={comment.id}>
                <GuideComment comment={comment} />
              </Collapse>
            ))}
        </TransitionGroup>
      </List>
    </>
  );
}
